import React, {useId} from 'react';
import {TextFieldProps} from '@mui/material';
import {TextField as PathTextField} from '@ellucian/react-design-system/core';
import {useField} from 'formik';

export type Props = Omit<TextFieldProps, 'error' | 'size'> & {
  label: string;
  error?: boolean | string;
  size?: 'small' | 'default';
  className?: string;
  cy?: string;
  testId?: string;
};

const EllucianTextInput = ({
  label,
  error,
  size = 'default',
  className,
  cy,
  testId,
  ...inputProps
}: Props) => {
  const errorId = useId();
  const hasError = !!error;

  return (
    <div>
      <PathTextField
        {...inputProps}
        className={className}
        label={label}
        size={size}
        error={hasError}
        inputProps={{
          'data-testid': testId || 'shared-text-input',
          'data-cy': cy,
          ...(hasError && {
            'aria-invalid': true,
            'aria-describedby': errorId,
          }),
          ...inputProps.inputProps,
        }}
      />
      {hasError && (
        <div
          id={errorId}
          className="shared-error-text"
          role="alert"
          data-testid="shared-text-input-error"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export const FormikEllucianTextInput = (props: {name: string} & Props) => {
  const [field, meta] = useField({name: props.name, type: props.type || 'text'});

  return (
    <EllucianTextInput
      {...props}
      {...field}
      error={meta.touched ? meta.error : undefined}
      {...(props.onChange && {
        onChange: (e) => {
          field.onChange(e);
          props.onChange?.(e);
        },
      })}
    />
  );
};

export default EllucianTextInput;
